/**
 * Kosten (E17) — reine Regeln, damit App und Tests dieselbe Rechnung sehen.
 *
 * Geld steht **in Cent als ganze Zahl**: 0,1 + 0,2 ist in Gleitkomma nicht
 * 0,3, und eine Monatssumme, die um einen Cent danebenliegt, sieht aus wie
 * ein Fehler — weil sie einer ist.
 *
 * Kein „wer schuldet wem“ (Timo, 15.10.2026): gezeigt wird, wer was
 * ausgegeben hat. Sparen ist keine Ausgabe, sondern ein Ziel mit Einzahlungen.
 */

import { stableId } from './ids.js';

export const EXPENSE_CATEGORIES = [
  'food',
  'home',
  'kids',
  'sport',
  'leisure',
  'mobility',
  'health',
  'other',
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const RECURRING_INTERVALS = ['monthly', 'yearly'] as const;
export type RecurringInterval = (typeof RECURRING_INTERVALS)[number];

export const EXPENSE_SOURCES = ['manual', 'shopping', 'recurring'] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];

/** Obergrenze je Betrag: 1 Mio. € — Tippfehler („125000“ statt „12,50“) fallen auf. */
export const MAX_AMOUNT_CENTS = 100_000_000;

/**
 * Betrag aus der Eingabe: „12,50“, „12.5“, „1.234,56“, „12“ → Cent.
 * `null` bei allem, was keine positive Summe ist — nie still 0.
 */
export function parseAmount(input: string): number | null {
  let text = input.trim().replace(/\s|€/g, '');
  if (text === '') return null;
  // „1.234,56“: Punkt als Tausender, Komma als Dezimal.
  if (text.includes(',') && text.includes('.')) text = text.replace(/\./g, '');
  text = text.replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const cents = Math.round(Number(text) * 100);
  return cents > 0 && cents <= MAX_AMOUNT_CENTS ? cents : null;
}

/** „2026-10“ zu einem Datum. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

export function shiftMonth(month: string, by: number): string {
  const [year, mon] = month.split('-').map(Number) as [number, number];
  const index = year * 12 + (mon - 1) + by;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

export interface CostEntry {
  amountCents: number;
  date: string;
  category: string;
  paidBy: string | null;
}

/** Summe je Kategorie für einen Monat — absteigend nach Betrag. */
export function sumByCategory(
  entries: readonly CostEntry[],
  month: string,
): { category: string; cents: number }[] {
  const sums = new Map<string, number>();
  for (const entry of entries) {
    if (monthOf(entry.date) !== month) continue;
    sums.set(entry.category, (sums.get(entry.category) ?? 0) + entry.amountCents);
  }
  return [...sums.entries()]
    .map(([category, cents]) => ({ category, cents }))
    .sort((a, b) => b.cents - a.cents);
}

/** Wer hat im Monat was ausgegeben — `null` (unbekannt) steht als eigener Posten da. */
export function sumByPerson(
  entries: readonly CostEntry[],
  month: string,
): { paidBy: string | null; cents: number }[] {
  const sums = new Map<string | null, number>();
  for (const entry of entries) {
    if (monthOf(entry.date) !== month) continue;
    sums.set(entry.paidBy, (sums.get(entry.paidBy) ?? 0) + entry.amountCents);
  }
  return [...sums.entries()]
    .map(([paidBy, cents]) => ({ paidBy, cents }))
    .sort((a, b) => b.cents - a.cents);
}

export function monthTotal(entries: readonly CostEntry[], month: string): number {
  return entries.reduce(
    (sum, entry) => (monthOf(entry.date) === month ? sum + entry.amountCents : sum),
    0,
  );
}

export type BudgetState = 'none' | 'ok' | 'warn' | 'over';

/** Ab 80 % aufgepasst, über 100 % drüber. Ohne Budget: keine Aussage. */
export function budgetState(spentCents: number, budgetCents: number | null): BudgetState {
  if (!budgetCents || budgetCents <= 0) return 'none';
  if (spentCents > budgetCents) return 'over';
  if (spentCents >= budgetCents * 0.8) return 'warn';
  return 'ok';
}

export interface RecurringRule {
  id: string;
  interval: RecurringInterval;
  /** Tag im Monat (1–31); fehlt der Tag im Monat (31. Februar), gilt der letzte. */
  day: number;
  /** Bei jährlich: der Monat (1–12). */
  month: number | null;
  startDate: string;
  endDate: string | null;
  active: boolean;
}

function lastDay(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Fälligkeiten einer wiederkehrenden Ausgabe zwischen `from` und `to`
 * (jeweils einschließlich). Rein rechnerisch — gebucht wird mit fester
 * Kennung (`recurringExpenseId`), damit zwei Geräte nicht zweimal buchen.
 */
export function recurringDueDates(rule: RecurringRule, from: string, to: string): string[] {
  if (!rule.active) return [];
  const dates: string[] = [];
  let cursor = monthOf(from);
  const last = monthOf(to);
  while (cursor <= last) {
    const [year, mon] = cursor.split('-').map(Number) as [number, number];
    if (rule.interval === 'monthly' || rule.month === mon) {
      const day = Math.min(rule.day, lastDay(year, mon));
      const date = `${cursor}-${String(day).padStart(2, '0')}`;
      if (
        date >= from &&
        date <= to &&
        date >= rule.startDate &&
        (!rule.endDate || date <= rule.endDate)
      ) {
        dates.push(date);
      }
    }
    cursor = shiftMonth(cursor, 1);
  }
  return dates;
}

/** Feste Kennung einer gebuchten Fälligkeit — dieselbe auf jedem Gerät. */
export function recurringExpenseId(ruleId: string, date: string): string {
  return stableId(`recurring:${ruleId}:${date}`);
}

/** Fortschritt eines Sparziels in Prozent (0–100), aus den Einzahlungen. */
export function savingsProgress(
  targetCents: number,
  deposits: readonly { amountCents: number }[],
): { savedCents: number; percent: number } {
  const savedCents = deposits.reduce((sum, deposit) => sum + deposit.amountCents, 0);
  const percent = targetCents > 0 ? Math.min(100, Math.round((savedCents / targetCents) * 100)) : 0;
  return { savedCents, percent };
}
